<script>
  import { onMount, createEventDispatcher } from "svelte";
  import { getRtcConfiguration } from "../turn.js";
  import { tuneVideoSender } from "../encoding.js";
  import { openSignalChannel, signalingEnabled } from "../signaling.js";
  import { callActive, declineUntil, DECLINE_COOLDOWN_MS } from "../callState.js";
  import { getDisplayNames } from "../profiles.js";
  import { log, announce } from "./Logging.svelte";
  import { playChime as chime } from "../chime.js";
  let videoElement;
  let audioElement;

  // The video sender and the camera track it carries, so the slow-camera relay can
  // swap what is sent.
  let videoSender = null;
  let localVideoTrack = null;

  export let connection = {
    roomName: null,
    key: null,
    destination: null,
    passphrase: null
  };
  export let stream;
  export let user;
  export let db;
  export let prefs = {};
  const dispatch = createEventDispatcher();

  let componentName = "PeerWindow";


  let peerConnection = null;
  let remoteStream = null;
  let qualityMonitor = null;
  let hungUp = false;
  let signalChannel = null;   // WebSocket relay channel when in use
  // Nobody is surprised by a window: a short overlay names the friend and offers Cancel
  // before the picture is shown. Cancel keeps this device unavailable for a while.
  // A window should open like a real one: the picture appears the moment it is
  // playing. The notice no longer holds the picture back; it stays on as a small
  // banner (with Cancel) over the picture for a few seconds instead.
  const OPENING_OVERLAY_MS = 0;
  const OPENING_BANNER_MS = 4000;
  // The first frames a decoder produces while the far end ramps up are sometimes
  // damaged (half-painted or wrongly coloured); skip a few of them.
  const FIRST_FRAMES_SETTLE_MS = 300;
  // How long each stage of opening took, logged and shown in the debug stats so a
  // slow open can be traced to detection, signalling, the connection or decoding.
  const openedAt = performance.now();
  let openMarks = [];
  let shownMarked = false;
  function mark(name) {
    const ms = Math.round(performance.now() - openedAt);
    openMarks = [...openMarks, name + " " + (ms / 1000).toFixed(1) + "s"];
    console.log(new Date().toISOString().slice(11, 23) + " open +" + ms + " ms: " + name);
    dispatch("debugStatus", { property: "Open time", value: openMarks.join(" | ") });
  }
  // The overlay stays until the friend's picture is actually playing and at least
  // OPENING_OVERLAY_MS have passed, so nobody ever sees the picture before the notice.
  let overlayMinElapsed = false;
  let remotePlaying = false;
  let cancelled = false;
  let bannerVisible = true;
  let bannerTimer = null;
  $: opening = !cancelled && !(overlayMinElapsed && remotePlaying);
  $: if (!opening && !cancelled && !shownMarked) {
    shownMarked = true;
    mark("picture shown");
    bannerTimer = setTimeout(() => (bannerVisible = false), OPENING_BANNER_MS);
  }
  let openingTimer = null;
  let friendName = "";

  function otherParticipant() {
    const list = Array.isArray(connection.participants) ? connection.participants : [];
    return list.find(id => id && id !== user.uid) || connection.destination || null;
  }

  async function lookupFriendName() {
    const other = otherParticipant();
    if (!other) return;
    try {
      const names = await getDisplayNames(db, [other]);
      friendName = names[other] || "";
    } catch (e) {
      friendName = "";
    }
  }

  function playChime() {
    chime(prefs);
  }

  function cancelOpening() {
    console.log("Window declined; staying unavailable for " + DECLINE_COOLDOWN_MS / 60000 + " minutes");
    declineUntil.set(Date.now() + DECLINE_COOLDOWN_MS);
    cancelled = true;
    hangUp();
  }

  function onPageHide() {
    hangUp();
  }
  const RELAY_ATTEMPTS = 3;
  const RELAY_RETRY_MS = 1500;

  // Signalling over the relay Worker (nothing stored).
  async function callViaRelay(isCaller) {
    const channel = await openSignalChannel(connection.roomName, user); // throws on failure
    if (hungUp) { channel.close(); return; }
    signalChannel = channel;
    console.log("Signalling via relay as " + (isCaller ? "caller" : "callee"));
    const configuration = await getRtcConfiguration(user, { forceRelay: !!prefs.forceRelay });
    peerConnection = new RTCPeerConnection(configuration);
    registerPeerConnectionListeners();
    addTunedTracks(peerConnection, stream);
    peerConnection.addEventListener("icecandidate", event => {
      if (!event.candidate) return;
      channel.send({ type: "candidate", candidate: event.candidate.toJSON() });
    });
    peerConnection.addEventListener("track", event => {
      console.log("Got remote track:", event.streams[0]);
      if (event.track && event.track.kind === "video") mark("remote video track");
      tuneReceiver(event.receiver);
      event.streams[0].getTracks().forEach(track => remoteStream.addTrack(track));
    });
    const pendingCandidates = [];
    let remoteSet = false;
    const addCandidate = async c => {
      try { await peerConnection.addIceCandidate(new RTCIceCandidate(c)); } catch (e) { console.warn("candidate rejected", e && e.message); }
    };
    channel.onMessage(async message => {
      if (hungUp || !peerConnection) return;
      if (message.type === "offer" && !isCaller) {
        await peerConnection.setRemoteDescription(new RTCSessionDescription(message.description));
        remoteSet = true;
        const answer = await peerConnection.createAnswer();
        await peerConnection.setLocalDescription(answer);
        channel.send({ type: "answer", description: { type: answer.type, sdp: answer.sdp } });
        for (const c of pendingCandidates.splice(0)) await addCandidate(c);
      } else if (message.type === "answer" && isCaller) {
        if (!peerConnection.currentRemoteDescription) {
          await peerConnection.setRemoteDescription(new RTCSessionDescription(message.description));
          remoteSet = true;
          for (const c of pendingCandidates.splice(0)) await addCandidate(c);
        }
      } else if (message.type === "candidate") {
        if (remoteSet) await addCandidate(message.candidate); else pendingCandidates.push(message.candidate);
      } else if (message.type === "peer-left") {
        console.log("The other side left the signalling room");
      }
    });
    if (isCaller) {
      const offer = await peerConnection.createOffer();
      await peerConnection.setLocalDescription(offer);
      channel.send({ type: "offer", description: { type: offer.type, sdp: offer.sdp } });
    }
  }

  async function startCall() {
    const isCaller = !connection.destination;
    mark(isCaller ? "calling" : "answering");
    // Signalling goes over the relay Worker only (nothing stored). The Firestore
    // Rooms path was retired on 2026-10-21; a relay that cannot be reached is retried
    // a few times, then the window gives up and the next match tries again.
    for (let attempt = 1; attempt <= RELAY_ATTEMPTS; attempt++) {
      if (hungUp) return;
      try {
        await callViaRelay(isCaller);
        return;
      } catch (err) {
        console.warn("Relay signalling attempt " + attempt + " failed:", err && err.message ? err.message : err);
        signalChannel = null;
        if (attempt < RELAY_ATTEMPTS) await new Promise(r => setTimeout(r, RELAY_RETRY_MS));
      }
    }
    console.error("Signalling relay unavailable; closing this window");
    hangUp();
  }

  async function hangUp(e) {
    if (hungUp) return;
    hungUp = true;
    callActive.set(false);
    if (signalChannel) {
      signalChannel.send({ type: "bye" });
      signalChannel.close();
      signalChannel = null;
    }
    if (remoteStream) {
      remoteStream.getTracks().forEach(track => track.stop());
    }

    if (peerConnection) {
      peerConnection.close();
    }

  }


  // Call quality tuning. A window should stay smooth rather than sharp: tell the
  // encoder the content is motion, let it drop resolution before frames under load,
  // prefer VP9 (better quality per bit than H.264 on home uplinks) with H.264 as the
  // fallback every browser shares, and give the receiver a small jitter buffer.
  // Each step is optional and ignored by browsers that lack it.
  function addTunedTracks(pc, localStream) {
    localStream.getTracks().forEach(track => {
      if (track.kind === "video") {
        try { track.contentHint = "motion"; } catch (e) { /* unsupported */ }
      }
      const sender = pc.addTrack(track, localStream);
      if (track.kind === "video") {
        videoSender = sender;
        localVideoTrack = track;
      }
      if (track.kind === "video") tuneVideoSender(sender);
    });
    try {
      const caps = window.RTCRtpSender && RTCRtpSender.getCapabilities ? RTCRtpSender.getCapabilities("video") : null;
      if (caps && caps.codecs && caps.codecs.length) {
        const first = prefs && prefs.videoCodec && prefs.videoCodec !== "auto" ? "video/" + prefs.videoCodec : null;
        const rank = c => {
          const m = (c.mimeType || "").toLowerCase();
          if (first && m === first) return -1;
          if (m === "video/vp9") return 0;
          if (m === "video/h264") return 1;
          if (m === "video/vp8") return 2;
          if (m === "video/av1") return 3;
          return 4; // rtx, red, ulpfec and friends keep their relative order after the codecs
        };
        const ordered = caps.codecs.slice().sort((a, b) => rank(a) - rank(b));
        pc.getTransceivers().forEach(t => {
          if (t.sender && t.sender.track && t.sender.track.kind === "video" && typeof t.setCodecPreferences === "function") {
            t.setCodecPreferences(ordered);
          }
        });
        console.log("Video codec preference: " + ordered.filter(c => rank(c) < 4).map(c => c.mimeType.replace("video/", "")).filter((v, i, a) => a.indexOf(v) === i).join(" > "));
      }
    } catch (e) {
      console.log("Codec preference not applied:", e && e.message ? e.message : e);
    }
  }

  function tuneReceiver(receiver) {
    try {
      if (receiver && "playoutDelayHint" in receiver) receiver.playoutDelayHint = 0.1;
    } catch (e) { /* unsupported */ }
  }

  function registerPeerConnectionListeners() {
    peerConnection.addEventListener("icegatheringstatechange", () => {
      console.log(
        `ICE gathering state changed: ${peerConnection.iceGatheringState}`
      );
    });

    peerConnection.addEventListener("connectionstatechange", () => {
      console.log(`Connection state change: ${peerConnection.connectionState}`);
    });

    peerConnection.addEventListener("signalingstatechange", () => {
      console.log(`Signaling state change: ${peerConnection.signalingState}`);
    });

    peerConnection.addEventListener("iceconnectionstatechange", () => {
      console.log(
        `ICE connection state change: ${peerConnection.iceConnectionState}`
      );
      if (peerConnection.iceConnectionState === "connected") {
        if (videoSender) tuneVideoSender(videoSender); // Firefox has encodings to cap only after negotiation
        mark("connected");
        logCallPath();
      }
    });
  }

  // Report whether the media is going direct or through the TURN relay.
  function logCallPath() {
    const pc = peerConnection;
    if (!pc || typeof pc.getStats !== "function") return;
    pc.getStats(null).then(reports => {
      const byId = {};
      reports.forEach(r => (byId[r.id] = r));
      let pair = null;
      reports.forEach(r => {
        if (r.type === "transport" && r.selectedCandidatePairId) pair = byId[r.selectedCandidatePairId];
      });
      if (!pair) {
        reports.forEach(r => {
          if (r.type === "candidate-pair" && (r.selected || r.nominated) && r.state === "succeeded") pair = r;
        });
      }
      if (!pair) return;
      const local = byId[pair.localCandidateId] || {};
      const remote = byId[pair.remoteCandidateId] || {};
      const viaRelay = local.candidateType === "relay" || remote.candidateType === "relay";
      console.log(
        "Call path: " + (viaRelay ? "relay (TURN)" : "direct") +
        " [local " + (local.candidateType || "?") + ", remote " + (remote.candidateType || "?") + "]"
      );
    }).catch(() => {});
  }

  function startQualityMonitor() {
    measureQuality();
    qualityMonitor = setInterval(function() {
      measureQuality();
    }, 1000);
  }

  function trackExists(track) {
    let exists = false;
    if (track !== undefined) {
      if (track.length > 0) {
        exists = true;
      }
    }
    return exists;
  }

  // Every 5 s, one console line per direction with the counters that explain a
  // stutter: frames dropped, freezes, keyframe requests, packet loss, and (on the
  // sending side) why the encoder is limiting quality.
  let lastStatsLog = 0;
  let prevIn = null;
  let prevOut = null;
  // Display-side diagnostics for the remote picture: media events the element fires
  // (a "resize" is the far end changing resolution) and gaps between presented
  // frames longer than DISPLAY_GAP_MS, which is what a visible stutter or flash is.
  const DISPLAY_GAP_MS = 65; // 1.5 frame intervals at 24 fps: catches a single skipped frame
  let displayWatchStopped = false;
  let displayGaps = 0;
  let displayGapMax = 0;
  let displayFrames = 0;
  function watchDisplay(el) {
    const ts = () => new Date().toISOString().slice(11, 23);
    ["resize", "waiting", "stalled", "emptied", "suspend", "pause", "loadedmetadata"].forEach(ev => {
      el.addEventListener(ev, () => {
        console.log(ts() + " video element " + ev + (ev === "resize" ? " " + el.videoWidth + "x" + el.videoHeight : ""));
      });
    });
    if (typeof el.requestVideoFrameCallback !== "function") return;
    let last = 0;
    let lastMedia = -1;
    const tick = (now, meta) => {
      if (displayWatchStopped) return;
      displayFrames++;
      // A gap is only the display's fault when it is longer than the spacing of the
      // frames themselves (mediaTime is the sender's timestamp): a far end sending
      // one frame a second is not a stutter here.
      const mediaTime = meta && typeof meta.mediaTime === "number" ? meta.mediaTime : -1;
      const sourceGap = lastMedia >= 0 && mediaTime >= 0 ? Math.max(0, (mediaTime - lastMedia) * 1000) : 0;
      const excess = last ? now - last - sourceGap : 0;
      if (excess > DISPLAY_GAP_MS) {
        displayGaps++;
        displayGapMax = Math.max(displayGapMax, excess);
        if (excess > 150) console.log(ts() + " display gap " + Math.round(excess) + " ms beyond the frame spacing");
      }
      last = now;
      lastMedia = mediaTime;
      el.requestVideoFrameCallback(tick);
    };
    el.requestVideoFrameCallback(tick);
  }


  // When the encoder is sending almost nothing, say why it might be: the camera
  // track's own state and rate, and whether the page is hidden or unfocused
  // (Firefox cuts a hidden page's camera to about one frame a second).
  // Some webcams deliver only a frame or two a second at times (long exposures in
  // dim light, USB 2 cameras in uncompressed modes). When the camera itself has
  // been the bottleneck for 15 s, stop sending the camera track directly and send a
  // canvas that repaints the camera's latest frame 24 times a second instead: the
  // far end then gets a steady stream rather than a slideshow.
  let slowSendStrikes = 0;
  let cameraRelayed = false;
  let relayVideo = null;
  let relayCanvas = null;
  let relayRaf = 0;
  function considerCameraRelay(reports, r) {
    if (cameraRelayed) return;
    let sourceFps = -1;
    reports.forEach(x => {
      if (x.type === "media-source" && x.kind === "video" && typeof x.framesPerSecond === "number") sourceFps = x.framesPerSecond;
    });
    const track = localVideoTrack || (stream && stream.getVideoTracks()[0]);
    const slow = track && (r.framesPerSecond || 0) < 5 && (sourceFps < 0 || sourceFps < 5);
    slowSendStrikes = slow ? slowSendStrikes + 1 : 0;
    if (slowSendStrikes < 3 || !videoSender) return;
    cameraRelayed = true;
    const st = track.getSettings ? track.getSettings() : {};
    console.log(new Date().toISOString().slice(11, 19) + " camera delivers " + (sourceFps >= 0 ? sourceFps : r.framesPerSecond || 0) + " fps at " + st.width + "x" + st.height + "; relaying its latest frame at 24 fps");
    relayVideo = document.createElement("video");
    relayVideo.muted = true;
    relayVideo.playsInline = true;
    relayVideo.srcObject = new MediaStream([track]);
    relayVideo.play().catch(() => {});
    relayCanvas = document.createElement("canvas");
    relayCanvas.width = st.width || 1280;
    relayCanvas.height = st.height || 720;
    const relayTrack = relayCanvas.captureStream(24).getVideoTracks()[0];
    const draw = () => {
      relayRaf = 0;
      if (!relayCanvas) return;
      if (relayVideo.videoWidth) {
        if (relayCanvas.width !== relayVideo.videoWidth || relayCanvas.height !== relayVideo.videoHeight) {
          relayCanvas.width = relayVideo.videoWidth;
          relayCanvas.height = relayVideo.videoHeight;
        }
        relayCanvas.getContext("2d").drawImage(relayVideo, 0, 0, relayCanvas.width, relayCanvas.height);
      }
      relayRaf = requestAnimationFrame(draw);
    };
    draw();
    videoSender.replaceTrack(relayTrack).catch(e => console.warn("Could not send the relayed camera:", e && e.message));
  }
  function stopCameraRelay() {
    if (relayRaf) cancelAnimationFrame(relayRaf);
    relayRaf = 0;
    if (relayVideo) { relayVideo.pause(); relayVideo.srcObject = null; relayVideo = null; }
    relayCanvas = null;
  }

  function slowSendDiagnostics(reports, r) {
    try {
      const track = localVideoTrack || (stream && stream.getVideoTracks()[0]);
      const st = track && track.getSettings ? track.getSettings() : {};
      // What the camera is actually delivering to the encoder (media-source stats),
      // how long each encode takes, and any cap set on the sender.
      let source = "";
      let encode = "";
      let caps = "";
      if (reports && r) {
        reports.forEach(x => {
          if (x.type === "media-source" && x.kind === "video" && (!r.mediaSourceId || x.id === r.mediaSourceId)) {
            source = " source " + (x.framesPerSecond !== undefined ? x.framesPerSecond + " fps" : "?") + " (" + (x.frames !== undefined ? x.frames + " frames" : "no frame count") + ")";
          }
        });
        if (r.totalEncodeTime !== undefined && r.framesEncoded) encode = " encode " + Math.round(r.totalEncodeTime / r.framesEncoded * 1000) + " ms/frame";
        if (r.qualityLimitationReason) encode += " limit " + r.qualityLimitationReason;
      }
      if (videoSender && videoSender.getParameters) {
        const enc = (videoSender.getParameters().encodings || [])[0] || {};
        caps = " caps fr=" + (enc.maxFramerate || "none") + " br=" + (enc.maxBitrate || "none") + " scale=" + (enc.scaleResolutionDownBy || "1") + (enc.active === false ? " inactive" : "");
      }
      return " | SLOW SEND: track " + (track ? track.readyState + (track.muted ? " muted" : "") + (track.enabled ? "" : " disabled") : "none") +
        " " + (st.width || "?") + "x" + (st.height || "?") + "@" + (st.frameRate || "?") + source + encode + caps +
        " hidden=" + document.hidden + " focus=" + document.hasFocus() + " vis=" + document.visibilityState;
    } catch (e) { return " | SLOW SEND: (no details: " + (e && e.message) + ")"; }
  }
  function logVideoStats(reports) {
    const now = Date.now();
    if (now - lastStatsLog < 5000) return;
    lastStatsLog = now;
    const stamp = new Date().toISOString().slice(11, 19);
    reports.forEach(r => {
      if (r.type === "inbound-rtp" && r.kind === "video") {
        const d = prevIn || {};
        console.log(
          stamp + " video in: " + (r.framesPerSecond || 0) + " fps " + (r.frameWidth || 0) + "x" + (r.frameHeight || 0) +
          " | decoded +" + ((r.framesDecoded || 0) - (d.framesDecoded || 0)) +
          " dropped +" + ((r.framesDropped || 0) - (d.framesDropped || 0)) +
          " freezes +" + ((r.freezeCount || 0) - (d.freezeCount || 0)) +
          " lost +" + ((r.packetsLost || 0) - (d.packetsLost || 0)) +
          " pli +" + ((r.pliCount || 0) - (d.pliCount || 0)) +
          " nack +" + ((r.nackCount || 0) - (d.nackCount || 0)) +
          " jitter " + (r.jitter !== undefined ? Math.round(r.jitter * 1000) + "ms" : "?") +
          (r.decoderImplementation ? " | " + r.decoderImplementation : "") +
          " | shown " + displayFrames + " gaps " + displayGaps + (displayGaps ? " (max " + Math.round(displayGapMax) + " ms)" : "")
        );
        displayFrames = 0;
        displayGaps = 0;
        displayGapMax = 0;
        prevIn = r;
      }
      if (r.type === "outbound-rtp" && r.kind === "video") {
        const d = prevOut || {};
        console.log(
          stamp + " video out: " + (r.framesPerSecond || 0) + " fps " + (r.frameWidth || 0) + "x" + (r.frameHeight || 0) +
          " | encoded +" + ((r.framesEncoded || 0) - (d.framesEncoded || 0)) +
          " sent +" + ((r.framesSent || 0) - (d.framesSent || 0)) +
          " keyframes +" + ((r.keyFramesEncoded || 0) - (d.keyFramesEncoded || 0)) +
          " limited by " + (r.qualityLimitationReason || "?") +
          (r.encoderImplementation ? " | " + r.encoderImplementation : "")
          + ((r.framesPerSecond || 0) < 5 ? slowSendDiagnostics(reports, r) : ""));
        considerCameraRelay(reports, r);
        prevOut = r;
      }
    });
  }

  function measureQuality() {
    if (peerConnection) {
      peerConnection.getStats(null).then(statsReports => {
        logVideoStats(statsReports);
        statsReports.forEach(statsReport => {
          if (statsReport.remoteSource) {
            if (statsReport.frameHeight && statsReport.frameWidth) {
              dispatch("debugStatus", {
                property: "Remote Resolution",
                value: statsReport.frameHeight + "x" + statsReport.frameWidth
              });
            }
            if (statsReport.framesPerSecond) {
              dispatch("debugStatus", {
                property: "Remote Frame Rate",
                value: statsReport.framesPerSecond
              });
            }
          } else {
            if (statsReport.frameHeight && statsReport.frameWidth) {
              dispatch("debugStatus", {
                property: "Local Resolution",
                value: statsReport.frameHeight + "x" + statsReport.frameWidth
              });
            }
            if (statsReport.framesPerSecond) {
              dispatch("debugStatus", {
                property: "Local Frame Rate",
                value: statsReport.framesPerSecond
              });
            }
          }
          // if(statsReport.kind == "video"){
          //   console.log("STATS",statsReport);
          // }
          // if (statsReport.type == "outbound-rtp" && statsReport.kind == "video") {

          // }
          // if (statsReport.type == "inbound-rtp" && statsReport.kind == "video") {
          //   if (statsReport.framesPerSecond) {
          //     dispatch("debugStatus", {
          //       property: "Remote Frame Rate",
          //       value: statsReport.framesPerSecond
          //     });
          //   }
          //   if (statsReport.frameHeight && statsReport.frameWidth) {
          //     dispatch("debugStatus", {
          //       property: "Remote Resolution",
          //       value:
          //         statsReport.frameHeight +
          //         "x" +
          //         statsReport.frameWidth
          //     });
          //   }
          // }
          // if (statsReport.type == "inbound-rtp" && statsReport.kind == "audio") {
          //   if (statsReport.jitter) {
          //     dispatch("debugStatus", {
          //       property: "Remote Audio Jitter",
          //       value: Number(statsReport.jitter.toFixed(2))
          //     });
          //   }
          // }
        });
      });
    }
  }

  onMount(() => {
    let functionName = "onMount";
    console.log("LOCAL STREAM", stream);
    announce("PeerWindow Mounted");
    callActive.set(true);
    window.addEventListener("pagehide", onPageHide);
    lookupFriendName();
    playChime();
    openingTimer = setTimeout(() => (overlayMinElapsed = true), OPENING_OVERLAY_MS);
    remoteStream = new MediaStream();
    videoElement.srcObject = remoteStream;
    watchDisplay(videoElement);
    console.log(connection);
    startCall();
    startQualityMonitor();
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      clearTimeout(openingTimer);
      clearTimeout(bannerTimer);
      dispatch("debugStatus", { property: "Open time", value: "", clear: true });
      displayWatchStopped = true;
      stopCameraRelay();
      callActive.set(false);
      hangUp();
      // Clear stats
      dispatch("debugStatus", {
        property: "Local Frame Rate",
        value: "",
        clear: true
      });
      dispatch("debugStatus", {
        property: "Local Resolution",
        value: "",
        clear: true
      });
      dispatch("debugStatus", {
        property: "Remote Frame Rate",
        value: "",
        clear: true
      });
      dispatch("debugStatus", {
        property: "Remote Resolution",
        value: "",
        clear: true
      });
      dispatch("debugStatus", {
        property: "Remote Audio Jitter",
        value: "",
        clear: true
      });
      if (qualityMonitor) {
        clearInterval(qualityMonitor);
      }
    };
  });
</script>

<style>
  .video-container {
    width: 100vw;
    height: 100vh;
    overflow-x: hidden;
  }
  .opening {
    position: fixed;
    inset: 0;
    z-index: 100000;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    background: var(--ink);
    color: var(--cream);
    text-align: center;
    padding: 2rem;
    font-family: Inter, system-ui, sans-serif;
  }
  .opening--banner {
    inset: auto 0 2rem 0;
    flex-direction: row;
    justify-content: center;
    background: transparent;
    padding: 0 1rem;
    pointer-events: none;
  }
  .opening__title--banner {
    font-size: clamp(1rem, 2vw, 1.4rem);
    padding: .5rem 1rem;
    border-radius: 999px;
    background: rgba(21, 16, 14, .6);
  }
  .opening__cancel--banner { margin-top: 0; padding: .45rem 1.1rem; font-size: 1rem; background: rgba(21, 16, 14, .6); pointer-events: auto; }
  .opening__eyebrow { margin: 0; letter-spacing: .2em; text-transform: uppercase; font-size: .8rem; opacity: .7; }
  .opening__title { margin: 0; font-size: clamp(1.6rem, 4vw, 3rem); font-family: Fraunces, Georgia, serif; }
  .opening__cancel {
    margin-top: 1rem;
    border: 1px solid var(--gold);
    background: transparent;
    color: var(--gold-hi);
    border-radius: 999px;
    padding: .7rem 1.6rem;
    font: inherit;
    font-size: 1.1rem;
    cursor: pointer;
  }
  .participantVideo {
    z-index: 99999;
    height: 100%;
    width: auto;
    max-width: none;
    margin-left: 50%;
    transform: translateX(-50%);
  }
</style>

{#if opening}
  <div class="opening" role="status" aria-live="polite">
    <p class="opening__eyebrow">PassCall</p>
    <p class="opening__title">Opening a window with {friendName || "a friend"}…</p>
    <button type="button" class="opening__cancel" on:click={cancelOpening}>Cancel</button>
  </div>
{:else if bannerVisible && !cancelled}
  <div class="opening opening--banner" role="status" aria-live="polite">
    <p class="opening__title opening__title--banner">Window open with {friendName || "a friend"}</p>
    <button type="button" class="opening__cancel opening__cancel--banner" on:click={cancelOpening}>Cancel</button>
  </div>
{/if}
<section class="hero has-background-black-ter is-fullheight">
  <div class="video-container">
    <video
      id="pVideo"
      class="participantVideo"
      bind:this={videoElement}
      height="100%"
      width="auto"
      autoplay
      playsinline
      on:playing={() => { mark("playing"); setTimeout(() => (remotePlaying = true), FIRST_FRAMES_SETTLE_MS); }} />
    <audio bind:this={audioElement} />
  </div>
</section>
